import { Archive, BookOpenText, ClipboardCheck, FilePlus2, LogOut, Search, X } from 'lucide-react'
import { type FormEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'

import type { DocumentRecord } from '../../shared/types'
import { ApiClientError, apiFetch } from '../api'
import { DocumentCard } from './DocumentCard'

interface DashboardProps {
  actor: string
  onOpen: (documentId: string) => void
  onLogout: () => void
}

type DialogState =
  | { kind: 'create' }
  | { kind: 'rename'; document: DocumentRecord }
  | { kind: 'delete'; document: DocumentRecord }
  | null

export function Dashboard({ actor, onOpen, onLogout }: DashboardProps) {
  const [documents, setDocuments] = useState<DocumentRecord[]>([])
  const [archived, setArchived] = useState(false)
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [error, setError] = useState('')
  const [dialog, setDialog] = useState<DialogState>(null)
  const dialogTrigger = useRef<HTMLElement | null>(null)

  function openDialog(next: Exclude<DialogState, null>, trigger: HTMLElement) {
    dialogTrigger.current = trigger
    setDialog(next)
  }

  function closeDialog() {
    const trigger = dialogTrigger.current
    setDialog(null)
    window.setTimeout(() => trigger?.focus(), 0)
  }

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    apiFetch<DocumentRecord[]>(`/api/documents?archived=${archived}`)
      .then((result) => {
        if (active) setDocuments(result)
      })
      .catch((reason) => {
        if (active) setError(reason instanceof ApiClientError ? reason.message : '文档列表加载失败')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [archived])

  const visible = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase()
    if (!normalized) return documents
    return documents.filter((document) =>
      `${document.title}\n${document.content}`.toLocaleLowerCase().includes(normalized),
    )
  }, [documents, query])

  async function create(title: string) {
    const created = await apiFetch<DocumentRecord>('/api/documents', {
      method: 'POST',
      body: JSON.stringify({ title, content: '' }),
    })
    closeDialog()
    onOpen(created.id)
  }

  async function rename(document: DocumentRecord, title: string) {
    const updated = await apiFetch<DocumentRecord>(`/api/documents/${document.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title, content: document.content, version: document.version }),
    })
    setDocuments((current) => current.map((item) => (item.id === updated.id ? updated : item)))
    closeDialog()
  }

  async function toggleArchive(document: DocumentRecord) {
    setError('')
    try {
      await apiFetch<DocumentRecord>(`/api/documents/${document.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ archived: !document.archived }),
      })
      setDocuments((current) => current.filter((item) => item.id !== document.id))
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : '归档操作失败')
    }
  }

  async function remove(document: DocumentRecord) {
    await apiFetch(`/api/documents/${document.id}`, { method: 'DELETE' })
    setDocuments((current) => current.filter((item) => item.id !== document.id))
    setDialog(null)
  }

  return (
    <main className="dashboard-page">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <span className="mini-mark">SHC</span>
          <span className="brand-copy"><strong>SHC Docs</strong><small>SHARED EDITORIAL DESK</small></span>
        </div>
        <div className="header-user">
          <div className="collaborator-info" aria-label={`当前协作者：${actor}`}>
            <span className="collaborator-avatar" aria-hidden="true">{actor.trim().slice(0, 2).toUpperCase()}</span>
            <span className="collaborator-meta"><small>当前协作者</small><strong><i aria-hidden="true" />{actor}</strong></span>
          </div>
          <button className="logout-button" type="button" onClick={onLogout} aria-label="退出登录" title="退出登录"><LogOut size={18} /></button>
        </div>
      </header>
      <section className="dashboard-hero">
        <div>
          <p className="eyebrow">SHARED WORKSPACE</p>
          <h1>文档面板</h1>
          <p>把未完成的想法放在这里，持续修改，也保留它们走过的路。</p>
        </div>
        <div className="dashboard-hero-actions">
          <a className="ghost-button tutorial-button" href="/annotations/3d-sitbench-workflow/"><ClipboardCheck size={19} />P0 工作流</a>
          <a className="ghost-button tutorial-button" href="/annotations/3d-sitbench-quality/"><ClipboardCheck size={19} />数据质检</a>
          <a className="ghost-button tutorial-button" href="/annotations/3d-sitbench-semantic-review/"><ClipboardCheck size={19} />语义复核</a>
          <a className="ghost-button tutorial-button" href="/annotations/3d-sitbench-formal/"><ClipboardCheck size={19} />标注校准</a>
          <a className="ghost-button tutorial-button" href="/annotations/3d-sitbench-v1/"><ClipboardCheck size={19} />先导标注</a>
          <a className="ghost-button tutorial-button" href="/tutorials/index.html"><BookOpenText size={19} />论文教程</a>
          <button className="primary-button create-button" type="button" onClick={(event) => openDialog({ kind: 'create' }, event.currentTarget)}><FilePlus2 size={19} />新建文档</button>
        </div>
      </section>
      <section className="dashboard-controls" aria-label="文档筛选">
        <div className="segmented-control">
          <button type="button" aria-pressed={!archived} onClick={() => setArchived(false)}>工作区</button>
          <button type="button" aria-pressed={archived} onClick={() => setArchived(true)}><Archive size={15} />归档</button>
        </div>
        <label className="search-control"><Search size={18} /><span className="sr-only">搜索文档</span><input type="search" aria-label="搜索文档" placeholder="搜索标题或正文…" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
      </section>
      {error ? <p className="page-error" role="alert">{error}</p> : null}
      {loading ? <div className="card-grid" aria-busy="true">{[1, 2, 3].map((item) => <div className="document-card skeleton" key={item} />)}</div> : null}
      {!loading && visible.length ? (
        <section className="card-grid" aria-label={archived ? '已归档文档' : '当前文档'}>
          {visible.map((document, index) => (
            <div className="card-reveal" style={{ '--delay': `${index * 45}ms` } as React.CSSProperties} key={document.id}>
              <DocumentCard document={document} onOpen={() => onOpen(document.id)} onRename={(event) => openDialog({ kind: 'rename', document }, event.currentTarget)} onArchive={() => void toggleArchive(document)} onDelete={(event) => openDialog({ kind: 'delete', document }, event.currentTarget)} />
            </div>
          ))}
        </section>
      ) : null}
      {!loading && !visible.length ? (
        <section className="dashboard-empty">
          <div className="empty-orbit"><FilePlus2 size={28} /></div>
          <h2>{query ? '没有匹配的文档' : archived ? '归档还是空的' : '从第一张纸开始'}</h2>
          <p>{query ? '换一个关键词，或者创建一份新文档。' : '写下一个标题，之后的内容可以慢慢长出来。'}</p>
          <button className="primary-button" type="button" onClick={(event) => openDialog({ kind: 'create' }, event.currentTarget)}>新建文档</button>
        </section>
      ) : null}
      {dialog?.kind === 'create' ? <TitleDialog title="新建文档" label="创建并打开" initial="" onCancel={closeDialog} onSubmit={create} /> : null}
      {dialog?.kind === 'rename' ? <TitleDialog title="重命名文档" label="保存名称" initial={dialog.document.title} onCancel={closeDialog} onSubmit={(title) => rename(dialog.document, title)} /> : null}
      {dialog?.kind === 'delete' ? <DeleteDialog document={dialog.document} onCancel={closeDialog} onDelete={() => remove(dialog.document)} /> : null}
    </main>
  )
}

function TitleDialog({ title, label, initial, onCancel, onSubmit }: { title: string; label: string; initial: string; onCancel: () => void; onSubmit: (title: string) => Promise<void> }) {
  const [value, setValue] = useState(initial)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!value.trim()) return setError('请输入文档标题')
    setSaving(true)
    try { await onSubmit(value.trim()) } catch (reason) { setError(reason instanceof ApiClientError ? reason.message : '保存失败，请重试'); setSaving(false) }
  }
  return <div className="modal-backdrop" onKeyDown={(event: KeyboardEvent) => { if (event.key === 'Escape') onCancel() }}><section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="title-dialog-heading"><button className="modal-close" type="button" onClick={onCancel} aria-label="关闭"><X size={19} /></button><h2 id="title-dialog-heading">{title}</h2><form onSubmit={submit}><label htmlFor="document-title">文档标题</label><input id="document-title" autoFocus maxLength={120} value={value} onChange={(event) => setValue(event.target.value)} />{error ? <p role="alert" className="form-error">{error}</p> : null}<div className="modal-actions"><button className="ghost-button" type="button" onClick={onCancel}>取消</button><button className="primary-button" type="submit" disabled={saving}>{saving ? '保存中…' : label}</button></div></form></section></div>
}

function DeleteDialog({ document, onCancel, onDelete }: { document: DocumentRecord; onCancel: () => void; onDelete: () => Promise<void> }) {
  const [error, setError] = useState('')
  return <div className="modal-backdrop" onKeyDown={(event: KeyboardEvent) => { if (event.key === 'Escape') onCancel() }}><section className="modal-card danger-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-dialog-heading"><h2 id="delete-dialog-heading">永久删除文档？</h2><p>“{document.title}”及其全部版本、留言和附件都会被删除，此操作无法撤销。</p>{error ? <p role="alert" className="form-error">{error}</p> : null}<div className="modal-actions"><button className="ghost-button" type="button" onClick={onCancel} autoFocus>取消</button><button className="danger-button" type="button" onClick={() => void onDelete().catch((reason) => setError(reason instanceof ApiClientError ? reason.message : '删除失败'))}>确认删除</button></div></section></div>
}
